import { addSeconds, formatDistanceStrict } from 'date-fns';
import { observer } from 'mobx-react';
import * as React from 'react';
import useStores from '../stores/UseStores';

interface CountdownProps {
  time: number;
  updateTime: (newTime: number) => void;
  className?: string;
  countdownCompleteCallback?: () => void;
}

const Countdown = (props: CountdownProps): React.JSX.Element => {
  const {
    languageStore: { currentLocale },
  } = useStores();
  const { time, updateTime, className, countdownCompleteCallback } = props;
  const [screenReaderText, setScreenReaderText] = React.useState<string>('');
  const prevCountdownTime = React.useRef<number>(time);
  const timerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (prevCountdownTime.current !== time) {
      setScreenReaderText('');
      prevCountdownTime.current = time;
    }
    createIntervalForTimer();

    return () => {
      clearTimer();
    };
  }, [time]);

  const createIntervalForTimer = (): void => {
    timerRef.current = window.setInterval(() => {
      const newTime = time !== 0 ? time - 1 : 0;
      if (newTime % 15 === 0) {
        updateScreenReaderText(newTime);
      }
      if (newTime === 0) {
        clearTimer();
        countdownCompleteCallback && time !== 0 && countdownCompleteCallback();
      }
      updateTime(newTime);
    }, 1000);
  };

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const updateScreenReaderText = (countdownTime: number): void => {
    const now = new Date();
    const text = formatDistanceStrict(addSeconds(now, countdownTime), now, { unit: 'second', locale: currentLocale });
    setScreenReaderText(text);
  };

  return (
    <span className={className}>
      <span className="sr-only" role="alert" aria-live="polite" aria-label={screenReaderText} />
      <span>{time}</span>
    </span>
  );
};

export default observer(Countdown);
